import { HttpClient, HttpParams } from "@angular/common/http";
import {
	DefaultDataService,
	EntityActionFactory,
	EntityCache,
	EntityOp,
	HttpUrlGenerator,
} from "@ngrx/data";
import { Store } from "@ngrx/store";
import { ApiPayload, ApiResponse, RecordTypeName } from "@recore-solutions/nstar-lib";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { dispatchRelatedRecordActions } from "./shared/shared.functions";

export class NstarDataService<T> extends DefaultDataService<T> {
	sinceDateCache: Map<string, Date> = new Map<string, Date>();

	constructor(
		entityName: RecordTypeName<T>,
		protected httpClient: HttpClient,
		httpUrlGenerator: HttpUrlGenerator,
		private store?: Store<EntityCache>,
		protected entityActionFactory?: EntityActionFactory
	) {
		super(entityName, httpClient, httpUrlGenerator);
	}

	getFlattenedRows<T>(
		schema: string,
		table: string,
		queryParams: Record<string, any>
	): Observable<T[]> {
		queryParams = { ...queryParams };
		queryParams.tableSchemaName = schema;
		queryParams.tableName = table;
		return this.getApiResponse<T>("dbo.spGetDynamicFlatDataAPI", queryParams).pipe(
			map((response) => response?.records || [])
		);
	}

	getApiResponse<T, S = null>(
		procName: string,
		queryParams: Record<string, any>,
		url = environment.endpoints.dynamicData
	): Observable<ApiResponse<T, S>> {
		const params = this.queryParamsToHttpParams(queryParams, { procName });
		return this.httpClient.get<ApiResponse<T, S>>(url, { params });
	}

	updateEntity<T>(entity: T, recordType: RecordTypeName<T>, endpoint: string): Observable<T> {
		const payload: ApiPayload<T> = {
			recordType,
			records: [entity],
		};
		return this.httpClient.post<ApiResponse<T>>(endpoint, payload).pipe(
			map((apiResponse) => apiResponse?.records),
			map((records) => records?.[0])
		);
	}

	/**
	 * Method to return a sinceDate for any given QueryParams object.
	 *
	 * If there are QueryParams, and if they have been used in a request previously, we only request changes since then.
	 * This method checks for previous requests with these QueryParams and returns a sinceDate string if found.
	 * It also sets the new sinceDate to the present.
	 */
	getSinceDate = (params?: Record<string, any>): string | null => {
		if (!params) {
			return null;
		}
		let sinceDate: string | null = null;
		const paramString = JSON.stringify(params);
		if (this.sinceDateCache.has(paramString)) {
			sinceDate = this.sinceDateCache.get(paramString)?.toISOString() || null;
		}
		this.sinceDateCache.set(paramString, new Date());
		return sinceDate;
	};

	queryParamsToHttpParams = (
		queryParams?: Record<string, any>,
		additionalProperties?: { [key: string]: any }
	): HttpParams => {
		const sinceDate = this.getSinceDate(queryParams) || "1900-01-01";
		const pageNumber = "1";
		const pageSize = "10000";
		return new HttpParams({
			fromObject: {
				sinceDate,
				pageNumber,
				pageSize,
				...queryParams,
				...(additionalProperties || {}),
			},
		});
	};

	setQueryParamsSinceDate = (params: Record<string, any>): Record<string, any> => {
		if (!params) {
			return params;
		}
		const sinceDate = this.getSinceDate(params);
		if (!!sinceDate) {
			return {
				...params,
				sinceDate,
			};
		}
		return params;
	};

	dispatchRelatedRecordActions = (
		payload: Pick<ApiPayload<any, any[]>, "related">,
		entityOp: EntityOp = EntityOp.ADD_MANY
	): void => {
		if (!!this.store && !!this.entityActionFactory) {
			dispatchRelatedRecordActions(this.store, this.entityActionFactory, entityOp)(payload);
		}
	};

	mapResponseToRecords = (response: ApiResponse<T>): T[] => response?.records || [];
}
